import { inject, Injectable } from '@angular/core';

import { STRING_CONSTANTS } from '@shared-constants';
import { NotePdf } from '@shared-models';
import { ElectronicNotePdfService } from '@shared-services';

import { IGraphQToBillyNote } from 'src/app/@interfaces/Billy/billy.interface';
import { INotasGrap } from 'src/app/@interfaces/notas.interface';
import { IValoresSingle } from 'src/app/@interfaces/valores.interface';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { OperationalReportingService } from 'src/app/@pages/reports/pages/operational-reporting/operational-reporting.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CalculateNotePdfService {
  private electronicNotePdfService = inject(ElectronicNotePdfService);
  private quotationService = inject(QuotationsService);
  private operationalReportingService = inject(OperationalReportingService);

  subtotal = 0;
  taxCalculated = 0;
  otherTaxes = 0;
  discount = 0;
  total = 0;
  qrUrl = '';

  noteObject = {} as INotasGrap;
  noteCount = {} as IGraphQToBillyNote;
  noteValues: IValoresSingle[] = [];

  async setNote(noteID: number, company: string, generate: boolean) {
    this.subtotal = 0;
    this.taxCalculated = 0;
    this.otherTaxes = 0;
    this.discount = 0;
    this.total = 0;
    this.noteObject = await this.getNote(noteID, company);
    this.qrUrl = this.generateDefaultQr(noteID, company);
    this.noteCount = this.noteObject.billyNotes![0];
    this.noteValues = this.noteObject.valor_notas;
    this.noteValues.forEach((element) => {
      const saleValues = this.calculateTotals(
        element,
        this.noteObject.invoice?.tax_incl ?? STRING_CONSTANTS.TRUE,
      );
      this.taxCalculated += saleValues.calculatedTax;
      this.otherTaxes += saleValues.calculatedOtherTax;
      this.subtotal += saleValues.subtotal;
      this.discount += saleValues.discount;
      this.total += saleValues.totals;
    });
    if (generate) {
      await this.generatePDFByType(company);
    }
  }

  private async generatePDFByType(company: string) {
    const invoiceObject: NotePdf = {
      open: true,
      noteObject: this.noteObject,
      noteCount: this.noteCount,
      noteValues: this.noteValues,
      subtotal: this.subtotal,
      taxCalculated: this.taxCalculated,
      otherTaxCalculated: this.otherTaxes,
      discount: this.discount,
      total: this.total,
      qrUrl: this.qrUrl,
      company: company,
    };
    await this.electronicNotePdfService.createNotePdf(invoiceObject);
  }

  private calculateTotals(element: IValoresSingle, taxIncluded: string) {
    let discount = 0;
    let subtotal = 0;
    let totals = 0;
    let calculatedTax = 0;
    let calculatedOtherTax = 0;
    let tax = 0;
    let price = 0;

    if (taxIncluded === STRING_CONSTANTS.TRUE) {
      // Case: price already includes tax
      tax = element.articulo[0].tax![0].value;
      price = element.precio / (tax + 1);
      subtotal = element.cantidad * price;
      discount = (element.descuento / 100) * subtotal;
    } else {
      // Case: price excluding tax
      price = element.precio;
      discount = (element.descuento / 100) * (element.cantidad * price);
      tax = this.operationalReportingService.getTaxPercent(
        element,
        this.noteObject.invoice!,
      );
    }
    subtotal = element.cantidad * price;
    subtotal = subtotal - discount;

    // 01 is the code for IVA, so all other taxes that are not IVA will be calculated.
    if (element.articulo[0].tax![0].dian_code !== '01') {
      calculatedOtherTax = subtotal * tax;
    } else {
      calculatedTax = subtotal * tax;
    }

    totals = subtotal + calculatedTax;

    return {
      calculatedTax,
      calculatedOtherTax,
      subtotal,
      discount,
      totals,
    };
  }

  private getNote(noteId: number, company: string) {
    return new Promise<INotasGrap>((resolve) => {
      this.quotationService.getSingleNote(noteId, company).subscribe((note) => {
        resolve(note);
      });
    });
  }

  private generateDefaultQr(noteId: number, company: string) {
    if (this.noteCount.cude) {
      return `${environment.dian_vpfe}/document/searchqr?documentkey=${this.noteCount.cude}`;
    }
    return `https://v1-insight.com/#/details_notas/${noteId}/${company}`;
  }
}
